import * as THREE from 'three'
import type { SplatData } from '@/plugins/gsplat/core/SplatData'

/**
 * 坐标系类型枚举。
 */
export enum CoordinateSystemType {
  /** Y 轴向上，Three.js / OpenGL 默认 */
  Y_UP = 'Y_UP',
  /** Y 轴向下，部分 3DGS 输出 */
  Y_DOWN = 'Y_DOWN',
  /** Z 轴向上，Blender / 3ds Max 默认 */
  Z_UP = 'Z_UP',
}

/**
 * 坐标系检测与转换工具。
 */
export class CoordinateSystem {
  /**
   * 检测坐标系类型，基于数据分布的启发式规则。
   *
   * @param splatData - 待检测的 SplatData
   * @returns 推断的坐标系类型；无数据时默认 Y_UP
   */
  static detect(splatData: SplatData): CoordinateSystemType {
    const x = splatData.getProp('x') as Float32Array
    const y = splatData.getProp('y') as Float32Array
    const z = splatData.getProp('z') as Float32Array

    if (!x || !y || !z || splatData.numSplats === 0) {
      return CoordinateSystemType.Y_UP // 默认
    }

    // 采样计算Y和Z的范围
    const sampleSize = Math.min(splatData.numSplats, 10000)
    const step = Math.floor(splatData.numSplats / sampleSize)

    let yMin = Infinity,
      yMax = -Infinity
    let zMin = Infinity,
      zMax = -Infinity

    for (let i = 0; i < splatData.numSplats; i += step) {
      yMin = Math.min(yMin, y[i])
      yMax = Math.max(yMax, y[i])
      zMin = Math.min(zMin, z[i])
      zMax = Math.max(zMax, z[i])
    }

    const yRange = yMax - yMin
    const zRange = zMax - zMin

    // 启发式判断：
    // - 如果Y范围明显大于Z范围，可能是Z_UP
    // - 如果大部分Y值为负，可能是Y_DOWN

    if (yRange > zRange * 2) {
      return CoordinateSystemType.Z_UP
    }

    // 检查Y值的符号分布
    let negativeYCount = 0
    for (let i = 0; i < splatData.numSplats; i += step) {
      if (y[i] < 0) negativeYCount++
    }

    if (negativeYCount > sampleSize * 0.7) {
      return CoordinateSystemType.Y_DOWN
    }

    return CoordinateSystemType.Y_UP
  }

  /**
   * 获取坐标系转换矩阵。
   *
   * @param from - 源坐标系
   * @param to - 目标坐标系
   * @returns 左乘矩阵；同坐标系返回单位矩阵
   */
  static getConversionMatrix(from: CoordinateSystemType, to: CoordinateSystemType): THREE.Matrix4 {
    const matrix = new THREE.Matrix4()

    if (from === to) {
      return matrix // 单位矩阵
    }

    // Y_DOWN -> Y_UP: 绕X轴旋转180度
    if (from === CoordinateSystemType.Y_DOWN && to === CoordinateSystemType.Y_UP) {
      matrix.makeRotationX(Math.PI)
    }
    // Y_UP -> Y_DOWN
    else if (from === CoordinateSystemType.Y_UP && to === CoordinateSystemType.Y_DOWN) {
      matrix.makeRotationX(Math.PI)
    }
    // Z_UP -> Y_UP: 绕X轴旋转-90度
    else if (from === CoordinateSystemType.Z_UP && to === CoordinateSystemType.Y_UP) {
      matrix.makeRotationX(-Math.PI / 2)
    }
    // Y_UP -> Z_UP
    else if (from === CoordinateSystemType.Y_UP && to === CoordinateSystemType.Z_UP) {
      matrix.makeRotationX(Math.PI / 2)
    }
    // Z_UP -> Y_DOWN
    else if (from === CoordinateSystemType.Z_UP && to === CoordinateSystemType.Y_DOWN) {
      matrix.makeRotationX(Math.PI / 2)
    }
    // Y_DOWN -> Z_UP
    else if (from === CoordinateSystemType.Y_DOWN && to === CoordinateSystemType.Z_UP) {
      matrix.makeRotationX(-Math.PI / 2)
    }

    return matrix
  }

  /**
   * 应用坐标转换到 SplatData（原地修改）。
   *
   * @param splatData - 目标数据，位置会被覆写
   * @param from - 源坐标系
   * @param to - 目标坐标系
   */
  static convert(splatData: SplatData, from: CoordinateSystemType, to: CoordinateSystemType): void {
    if (from === to) return

    const matrix = CoordinateSystem.getConversionMatrix(from, to)

    const x = splatData.getProp('x') as Float32Array
    const y = splatData.getProp('y') as Float32Array
    const z = splatData.getProp('z') as Float32Array

    if (!x || !y || !z) return

    // 转换位置
    const pos = new THREE.Vector3()
    for (let i = 0; i < splatData.numSplats; i++) {
      pos.set(x[i], y[i], z[i])
      pos.applyMatrix4(matrix)
      x[i] = pos.x
      y[i] = pos.y
      z[i] = pos.z
    }

    // 转换四元数
    const rot0 = splatData.getProp('rot_0') as Float32Array
    const rot1 = splatData.getProp('rot_1') as Float32Array
    const rot2 = splatData.getProp('rot_2') as Float32Array
    const rot3 = splatData.getProp('rot_3') as Float32Array

    if (rot0 && rot1 && rot2 && rot3) {
      const quat = new THREE.Quaternion()
      const rotationQuat = new THREE.Quaternion().setFromRotationMatrix(matrix)

      for (let i = 0; i < splatData.numSplats; i++) {
        // PLY 存储格式: rot_0=w, rot_1=x, rot_2=y, rot_3=z (标量在前)
        // THREE.Quaternion.set() 期望: (x, y, z, w)
        quat.set(rot1[i], rot2[i], rot3[i], rot0[i])
        quat.premultiply(rotationQuat)
        quat.normalize()

        // 写回时也需要按 PLY 格式
        rot0[i] = quat.w
        rot1[i] = quat.x
        rot2[i] = quat.y
        rot3[i] = quat.z
      }
    }
  }
}
